<template>
  <div>
    <vs-popup :active.sync="is2FaPopupActive" title="Confirma que eres tú" class="header-title" style="z-index: 54000;">
      <div class="flex flex-col gap-4 py-2 px-10">

        <!-- Custom indication message -->
        <div class="text-lg" v-if="provider === 'googleAuth' && customIndicationMessage">{{ customIndicationMessage }}
        </div>
        <div class="text-lg" v-else v-html="actions[provider].label(this, user.phone) ?? 'Accion no disponible'">
        </div>
        <!-- Custom indication message end -->

        <!-- Code input -->
        <div class="w-1/2">
          <otp-input ref="otp" v-model="code" :length="6" :disabled="validating" @submit="sendCode" />
        </div>
        <!-- Code input end -->

        <!-- Google Auth Error message -->
        <vs-alert v-if="errorMssg && provider === 'googleAuth'" color="danger" class="mt-3 mb-3" style="height: auto">
          {{ errorMssg }}
          <div class="black" v-if="showInstructions">
            <br>Si el problema persiste, es posible que tu app de autenticación
            (Autenticador de Google, Authy, etc.) no esté sincronizada.<br><br>
            Intenta <a :href="$sanitizeUrl(twoFactorCodeUrl)" target="_blank" rel="noopener noreferrer"
              class="underline bold black"> sincronizar tu app </a> de autenticación o <a
              :href="$sanitizeUrl('perfil/seguridad/2fa')" rel="noopener noreferrer"
              class="underline bold black">reestablece tu segundo factor de autenticación</a>.
          </div>
        </vs-alert>
        <!-- Google Auth Error message end -->

        <!-- Resend timer -->
        <div class="flex gap-2 text-sm" v-if="actions[provider].resentTimer > 0">
          ¿No lo recibiste?
          <label v-if="resendEnabled" class="cursor-pointer underline" @click="requestCode">Reenviar</label>
          <template v-else>
            Reenviar en<span class="bold">{{ resendTimer }}s </span>
          </template>
        </div>
        <!-- Resend timer end -->

        <!-- Continue button -->
        <div>
          <vs-button :id="buttonId" :data-projectid="currentProjectId" class="statement-btn" color="primary"
            :disabled="code == null || code.length !== 6" @click="sendCode">Continuar</vs-button>
        </div>
        <!-- Continue button end -->

        <!-- Choose other method -->
        <div>
          <label class="cursor-pointer black underline" @click="showExtraOptions = !showExtraOptions">
            Elegir otro método
          </label>
        </div>
        <!-- Choose other method end -->

        <!-- Choose other method content -->
        <div v-if="showExtraOptions" class="flex flex-col gap-1">
          <div class="flex items-center" v-for="p in Object.keys(actions)"
            v-if="actions[p].canShow(user) && p !== provider">
            <vs-icon v-if="actions[p].isIcon" :icon="actions[p].icon" size="small" color="primary" />
            <image-from-source v-else :path="actions[p].icon" :style="actions[p].style" />

            <label class="cursor-pointer underline black ml-2" @click="changeProvider(p)">
              {{ actions[p].name }}<br>
            </label>
          </div>
        </div>
      </div>
      <!-- Choose other method content end -->

    </vs-popup>
  </div>
</template>

<script>
import formatHelper from "@mixins/formatHelper";
import OtpInput from "@components/OtpInput.vue";

export default {
  name: "TwoFactorAuth",
  mixins: [formatHelper],
  components: {
    OtpInput
  },
  props: {
    provider: {
      type: String,
      required: false,
      default: 'googleAuth'
    },
    userId: {
      type: [String, Number],
      required: true
    },
    customIndicationMessage: {
      type: String,
      required: false,
      default: null
    },
    customErrorMessage: {
      type: String,
      required: false,
      default: null
    },
    currentProjectId: {
      type: [String, Number],
      required: false,
      default: null
    },
    buttonId: {
      type: String,
      required: false,
      default: null
    }
  },
  data: () => ({
    actions: {
      'sms': {
        name: 'Mensaje de texto (SMS)',
        icon: 'sms',
        isIcon: true,
        style: '',
        label: (context, phone) => {
          const last4Chars = context.visibleLastNChars(phone, 4);
          return `Ingresa los 6 dígitos del código que enviamos por <strong>SMS</strong> al número <strong>${last4Chars}</strong>`;
        },
        resentTimer: 60,
        canShow: (user) => {
          return user.phone !== null;
        },
        requestCode: async () => {
          await axios.post('api/auth/mfa/request-code', { challenge: 'sms' })
        },
        validateCode: async (code) => {
          await axios.post('api/auth/mfa/verify-code', { challenge: 'sms', code: code, scopes: ['device:manage'] })
        }
      },
      'whatsapp': {
        name: 'WhatsApp',
        icon: '/img/wp-green.png',
        isIcon: false,
        style: 'width:24px;',
        label: (context, phone) => {
          const last4Chars = context.visibleLastNChars(phone, 4);
          return `Ingresa los 6 dígitos del código que enviamos por <strong>WhatsApp</strong> al número <strong>${last4Chars}</strong>`;
        },
        resentTimer: 60,
        canShow: (user) => {
          return user.phone !== null;
        },
        requestCode: async () => {
          await axios.post('api/auth/mfa/request-code', { challenge: 'whatsapp' })
        },
        validateCode: async (code) => {
          await axios.post('api/auth/mfa/verify-code', { challenge: 'whatsapp', code: code, scopes: ['device:manage'] })
        }
      },
      'googleAuth': {
        name: 'App de autentificación',
        icon: '/img/google-icon.png',
        isIcon: false,
        style: 'width:21px;',
        label: (val) => {
          return `Ingresa los 6 dígitos del código que enviamos por <strong>Google Authenticator</strong>`;
        },
        resentTimer: 0,
        requestCode: () => { },
        canShow: (user) => {
          return user.google2fa_secret !== null;
        },
        validateCode: async (code) => {
          await axios.post(`api/auth/mfa/verify-code`, { challenge: 'google_authenticator', code: code, scopes: ['device:manage'] });
        }
      }
    },
    code: null,
    errorMssg: null,
    validating: false,
    twoFactorCodeUrl: "https://soporte.redgirasol.com/support/solutions/articles/48001243819-el-c%C3%B3digo-de-mi-segundo-factor-de-autenticaci%C3%B3n-es-incorrecto-qu%C3%A9-puedo-hacer-",
    showInstructions: false,
    showExtraOptions: false,
    alternativeCodeSent: false,
    whatsappSent: false,
    resendTimer: 60,
    timer: null,
    resendEnabled: false
  }),
  watch: {
    is2FaPopupActive(n) {
      if (n) {
        this.requestCode();

        this.validating = false;
        this.errorMssg = null;
        this.code = null;
        this.showInstructions = false;
        this.showExtraOptions = false;
        this.alternativeCodeSent = false;
        this.whatsappSent = false;
        this.timer = null;
        this.resendTimer = 60;
        this.resendEnabled = false;
      }
    }
  },
  computed: {
    is2FaPopupActive: {
      get() {
        return this.$store.state.is2faPopupActive;
      },
      set(val) {
        this.$store.commit("TOGGLE_IS_2FA_POPUP_ACTIVE", val);
      }
    }
  },
  methods: {
    async sendCode() {
      this.errorMssg = null;

      if (this.code === null || this.code.length !== 6) {
        this.errorMssg = "Ingresa un código válido de 6 dígitos.";
        return;
      }

      const newCode = this.code;
      this.code = null;
      this.showLoading(true);

      if (this.provider !== 'googleAuth') {
        this.showInstructions = false;
      }

      try {
        await this.actions[this.provider].validateCode(newCode);
        this.$emit('on-code-sent', newCode);
        this.$emit('on-code-validated-provider', this.provider);
        this.$emit('on-code-validated', newCode);
        this.is2FaPopupActive = false;
      } catch (e) {
        this.$emit('on-code-invalid', newCode);
        this.errorMssg = "El código es incorrecto. Intenta nuevamente.";
        this.$refs.otp.focus()  // llama al método focus() del componente
        this.showInstructions = true;
      } finally {
        this.showLoading(false);
      }
    },
    async requestCode() {
      this.showLoading(true);
      this.errorMssg = null;
      this.showExtraOptions = false;
      this.$refs.otp.focus()  // llama al método focus() del componente
      this.setUpTimerConfig();
      this.alternativeCodeSent = true;

      try {
        await this.actions[this.provider].requestCode();
      } catch (e) {
        this.error_message =
          "Ocurrió  un error al obtener los datos, inténtalo mas tarde.";
      } finally {
        this.showLoading(false);
      }
    },
    changeProvider(provider) {
      this.provider = provider;
      this.alternativeCodeSent = false;
      this.requestCode();
    },
    setUpTimerConfig() {
      window.clearInterval(this.timer);
      this.resendEnabled = false;
      this.resendTimer = 60;
      
      this.$nextTick(() => {
        this.initNipCounter();
      });

    },
    initNipCounter() {
      window.clearInterval(this.timer);

      this.timer = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        }

        if (this.resendTimer <= 0) {
          window.clearInterval(this.timer);
          this.timer = null;
          this.resendEnabled = true;
        }
      }, 1000);
    },
  },
};
</script>